import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { LoadingSpinner } from './components/Skeleton';
import SkipLink from './components/SkipLink';
import { Analytics } from '@vercel/analytics/react';

// Lazy loading dos componentes
const LoginPage = lazy(() => import('./components/LoginPage'));
const PublicBooking = lazy(() => import('./components/PublicBooking'));
const PublicReview = lazy(() => import('./components/PublicReview'));
const ClientPortalLogin = lazy(() => import('./components/ClientPortalLogin'));
const ClientPortalAccount = lazy(() => import('./components/ClientPortalAccount'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));

// Componente de Loading para Suspense
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
    <div className="text-center">
      <LoadingSpinner size="xl" />
      <p className="mt-4 text-gray-600 dark:text-gray-400 font-medium">A carregar...</p>
    </div>
  </div>
);

// Componente para proteger rotas administrativas
const ProtectedRoute = ({ children }) => {
  const { isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  return isAdmin ? children : <Navigate to="/admin/login" />;
};

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
            <SkipLink targetId="main-content" />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Rota pública para marcações */}
                <Route path="/" element={<PublicBooking />} />
                <Route path="/agendar" element={<PublicBooking />} />
                
                {/* Rota pública para reviews */}
                <Route path="/review" element={<PublicReview />} />

                {/* Portal do cliente */}
                <Route path="/cliente/login" element={<ClientPortalLogin />} />
                <Route path="/cliente/conta" element={<ClientPortalAccount />} />
                
                {/* Acesso unificado e passo de password do admin */}
                <Route path="/admin/login" element={<Navigate to="/cliente/login" replace />} />
                <Route path="/admin/password" element={<LoginPage />} />
                
                {/* Rotas administrativas protegidas */}
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Suspense>
            <Analytics />
          </Router>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
